<template>
  <panel-layout
    id="alerts-panel"
    :loading="!!(alerts && alerts.loading) || false"
    :heading="heading"
    :show-footer="false"
  >
    <template v-if="alerts && counts" #heading>
      <div class="flex items-center space-x-3">
        <h2
          class="focus:outline-none text-xl tracking-tight font-semibold text-gray-800 items-center dark:text-gray-100"
        >
          {{ $t('pages.alerts.index.unreadAlerts',{ count: counts.unread }) }}
        </h2>

        <o-chip>{{ $t('pages.alerts.index.total', { count: alerts.total }) }}</o-chip>
      </div>
    </template>

    <template #actions-right>
      <div class="flex items-center space-x-2">
        <segment-field v-model="_filter" :options="filters" />
        <o-tooltip :text="$t('pages.alerts.index.markAllAsRead')">
          <o-button icon="markRead" flat @click="handleMarkAllRead" />
        </o-tooltip>

        <o-tooltip :text="$t('pages.alerts.index.deleteAll')">
          <o-button icon="delete" flat @click="handleDeleteAll" />
        </o-tooltip>
      </div>
    </template>

    <div class="h-full flex flex-col">
      <div v-if="notifications.length" class="p-5">
        <div
          v-for="(notification, id) in notifications"
          :key="id"
          :class="[
            'w-full py-3 px-6 flex items-center justify-between mb-3 last:mb-0 rounded-md',
            { 'bg-blue-600': notification.type === 'info' },
            { 'bg-orange-500': notification.type === 'warning' },
            { 'bg-red-600': notification.type === 'danger' }
          ]"
        >
          <div class="flex-shrink">
            <p
              v-if="notification.title"
              class="text-white text-sm font-medium leading-none mb-1"
            >
              {{ notification.title }}
            </p>
            <p
              v-if="notification.content"
              class="text-white/75 text-sm leading-none"
            >
              {{ notification.content }}
            </p>
          </div>

          <div class="flex flex-shrink-0 items-center space-x-2">
            <a
              v-if="demo"
              href="https://ovatu.com/create"
              target="_blank"
              class="px-4 py-2 bg-white/15 text-white text-sm rounded-xl hover:shadow-lg hover:scale-101 cursor-pointer transition"
            >
              {{ $t('pages.alerts.index.createAnAccount') }}
            </a>

            <o-internal-link
              v-if="notification.action && notification.action.internalUrl"
              :link="notification.action.internalUrl"
              class="px-4 py-2 bg-white/15 text-white text-sm rounded-xl hover:shadow-lg hover:scale-101 cursor-pointer transition"
            >
              {{ notification.action.title }}
            </o-internal-link>

            <a
              v-else-if="notification.action && notification.action.url"
              :href="notification.action.url"
              target="_blank"
              class="px-4 py-2 bg-white/15 text-white text-sm rounded-xl hover:shadow-lg hover:scale-101 cursor-pointer transition"
            >
              {{ notification.action.title }}
            </a>
          </div>
        </div>
      </div>

      <o-scroll v-if="alerts" class="h-10 flex-grow p-5">
        <div
          v-if="alerts.loading"
          class="absolute inset-0 h-full w-full bg-white/50 p-6 flex items-center justify-center dark:bg-gray-900"
        >
          <o-loader class="h-8 w-8" />
        </div>
        <p v-if="alerts.total === 0" class="p-4 text-sm text-gray-600">
          {{ $t('pages.alerts.index.noAlerts') }}
        </p>
        <template v-for="alert in alerts.items" :key="alert.idAlert">
          <o-internal-link
            :link="alert.primaryAction ? alert.primaryAction.internalUrl : ''"
            class="w-full p-4 mb-2 flex justify-between flex-shrink-0 border rounded-lg bg-gray-50/50 dark:bg-gray-800/50"
            @click="handleAlertClick(alert)"
          >
            <div
              class="flex"
              :class="{ 'opacity-50': alert.readForCurrentUser }"
            >
              <div class="w-full">
                <div class="flex items-center justify-between w-full">
                  <p
                    class="focus:outline-none leading-none text-gray-700 font-medium mb-1 dark:text-gray-200"
                  >
                    {{ alert.titleLabel }}
                  </p>
                </div>
                <p class="focus:outline-none text-sm text-gray-700 mb-2 dark:text-gray-200">
                  {{ alert.infoLabel }}
                </p>
                <p class="focus:outline-none text-xs text-gray-500 dark:text-gray-400 leading-none">
                  {{ alert.dateLabel }}
                </p>
              </div>
            </div>

            <div class="flex items-center">
              <o-tooltip :text="$t('general.markRead')">
                <o-button
                  variant="primary"
                  icon="markRead"
                  size="sm"
                  flat
                  :disabled="alert.readForCurrentUser || !!markReadPending[alert.idAlert]"
                  @click.stop.prevent="handleMarkRead(alert)"
                />
              </o-tooltip>

              <o-tooltip :text="$t('general.remove')">
                <o-button
                  variant="danger"
                  icon="delete"
                  size="sm"
                  flat
                  @click.stop.prevent="handleDelete(alert)"
                />
              </o-tooltip>
            </div>
          </o-internal-link>
        </template>
      </o-scroll>

      <div>
        <o-pagination
          v-if="alerts"
          v-model:pager="alerts"
          container-class="py-3 border-t flex justify-between items-center flex-shrink-0 px-4"
        />
      </div>
    </div>

    <delete-dialog
      ref="deleteDialog"
      @success="handleDeleteSuccess"
    />

    <delete-all-dialog
      ref="deleteAllDialog"
      @success="handleDeleteSuccess"
    />
  </panel-layout>
</template>
<script setup lang="ts">
// @ts-ignore
import DeleteDialog from '@/components/alert/actions/Delete.vue'
// @ts-ignore
import DeleteAllDialog from '@/components/alert/actions/DeleteAll.vue'

import { TablePager } from '@/_api/lib/pager'

// @ts-ignore
import { OPagination, OInternalLink } from '@/components/elements'

// @ts-ignore
import { SegmentField } from '@/components/fields'

// @ts-ignore
import PanelLayout from '@/components/panel/PanelLayout.vue'

import { usePanel } from '~/composables/usePanel'

const panel = usePanel()

const props = defineProps<{ modelType?: string }>()

const { $api, $notification, $auth, $panel, $i18n } = useNuxtApp()

const uiStore = useUIStore()
const alertStore = useAlertStore()

const deleteDialog = ref<any>()
const deleteAllDialog = ref<any>()

const alerts = ref<any>(null)
const active = ref(false)
const showRead = ref(false)
const markReadPending = ref<Record<string, boolean>>({})
const alertsSyncRetryTimeout = ref<ReturnType<typeof setTimeout> | null>(null)
const alertsSyncTimeout = ref<ReturnType<typeof setTimeout> | null>(null)
const alertsSyncToken = ref(0)

const ALERTS_RECONCILIATION_DELAY = 2000

const filters = [
  {
    label: $i18n.t('general.all'),
    value: false,
  },
  {
    label: $i18n.t('pages.alerts.index.unread'),
    value: 'unread',
  },
  {
    label: $i18n.t('pages.alerts.index.read'),
    value: 'read',
  },
]

const counts = computed(() => alertStore.counts)

const _filter = computed({
  get () {
    return uiStore.alerts?.filter || false
  },
  set (value: any) {
    uiStore.setAlerts({ key: 'filter', value })
  },
})

const heading = computed(() => $i18n.t('pages.alerts.index.history', { label: props.modelType }))

const headers = computed(() => {
  const h = [$i18n.t('pages.alerts.index.action'), $i18n.t('pages.alerts.index.changed'), $i18n.t('general.user')]

  if (isAdmin.value) {
    h.push($i18n.t('general.revert'))
  }

  return h
})

const notifications = computed(() => $auth.info?.panels || [])
const isAdmin = computed(() => false)
const demo = computed(() => false)

watch(() => _filter.value, () => {
  clearAlertsSync()
  alerts.value.reload()
})

const fetchAlertCounts = () => {
  return alertStore.fetchAlertCounts()
}

const fetchAlerts = async () => {
  try {
    await alerts.value.load()
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
  }
}

const handleDelete = (alert: any) => {
  deleteDialog.value.open(alert)
}

const handleDeleteAll = () => {
  deleteAllDialog.value.open()
}

const handleDeleteSuccess = () => {
  clearAlertsSync()
  alerts.value.reload()
  fetchAlertCounts()
}

const clearAlertsSyncRetryTimeout = () => {
  if (alertsSyncRetryTimeout.value) {
    clearTimeout(alertsSyncRetryTimeout.value)
    alertsSyncRetryTimeout.value = null
  }
}

const clearAlertsSyncTimeout = () => {
  if (alertsSyncTimeout.value) {
    clearTimeout(alertsSyncTimeout.value)
    alertsSyncTimeout.value = null
  }
}

const clearAlertsSync = () => {
  clearAlertsSyncRetryTimeout()
  clearAlertsSyncTimeout()
  alertsSyncToken.value++
}

const hasPendingMarkReadRequests = () => {
  return Object.keys(markReadPending.value).length > 0
}

const scheduleAlertsSync = (token: number) => {
  clearAlertsSyncTimeout()
  alertsSyncTimeout.value = setTimeout(() => {
    alertsSyncTimeout.value = null
    syncAlertsAndCounts(token)
  }, ALERTS_RECONCILIATION_DELAY)
}

const scheduleAlertsSyncRetry = (token: number) => {
  clearAlertsSyncRetryTimeout()
  alertsSyncRetryTimeout.value = setTimeout(() => {
    alertsSyncRetryTimeout.value = null
    syncAlertsAndCounts(token)
  }, 300)
}

const reloadAlertsPage = async () => {
  alerts.value.items = []
  alerts.value.stored = {}

  await alerts.value.load(alerts.value.page || 1)
}

const syncAlertsAndCounts = async (token = alertsSyncToken.value) => {
  if (token !== alertsSyncToken.value) {
    return
  }

  if (hasPendingMarkReadRequests()) {
    scheduleAlertsSyncRetry(token)
    return
  }

  try {
    await reloadAlertsPage()
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
  }

  fetchAlertCounts()
}

const handleAlertClick = async (alert: any) => {
  active.value = false

  await markAlertRead(alert, { notifyOnError: false })

  const reservation = alert?.primaryAction?.data?.reservation

  if (reservation) {
    // Close this alerts panel after handing off to the reservation it opened.
    panel.close(true)
  }
}

const markAlertRead = async (alert: any, { notifyOnError = true } = {}) => {
  const idAlert = alert?.idAlert

  if (!idAlert || alert.readForCurrentUser || markReadPending.value[idAlert]) {
    return false
  }

  markReadPending.value[idAlert] = true

  try {
    const response = await $api.alert.markRead(alert)

    if (response.success) {
      alert.readForCurrentUser = true
      alertStore.decrementUnreadCount()

      if (_filter.value === 'unread') {
        alerts.value.items = alerts.value.items.filter((item: any) => item.idAlert !== idAlert)

        if (typeof alerts.value.total === 'number' && alerts.value.total > 0) {
          alerts.value.total--
        }
      }

      scheduleAlertsSync(alertsSyncToken.value)

      return true
    }

    return false
  } catch (e: any) {
    console.error(e)

    if (notifyOnError) {
      $notification.error({ errors: e?.errors })
    }

    return false
  } finally {
    delete markReadPending.value[idAlert]
  }
}

const handleMarkRead = async (alert: any) => {
  await markAlertRead(alert)
}

const handleMarkAllRead = async () => {
  try {
    const success = await $api.alert.markAllRead()

    if (success) {
      $notification.toast({
        title: $i18n.t('pages.alerts.index.success'),
        text: $i18n.t('pages.alerts.index.successInfo'),
        variant: 'success',
      })

      clearAlertsSync()
      alerts.value.reload()
      fetchAlertCounts()
    }
  } catch (e: any) {
    $notification.error({ errors: e?.errors })
  }
}

onMounted(() => {
  alerts.value = new TablePager(
    (page: any) => $api.alert.filtered({ page, filter: _filter.value }),
    20,
  )

  fetchAlerts()
  fetchAlertCounts()
})

onBeforeUnmount(() => {
  clearAlertsSync()
})
</script>
